// Static industry list — mirrors industry_categories table for instant render
// in onboarding without a fetch. The DB seeds the same 50 entries.

export interface DefaultService {
  name: string;
  price: number;
  unit?: string; // "each", "hr", "sq ft" etc
}

export interface Industry {
  slug: string;
  name: string;
  group: string;
  defaultServices?: DefaultService[];
}

export const INDUSTRIES: Industry[] = [
  { slug: "cleaning", name: "Cleaning Services", group: "Field Service", defaultServices: [
    { name: "Standard Clean", price: 120 },
    { name: "Deep Clean", price: 250 },
    { name: "Move-Out Clean", price: 400 },
    { name: "Office Cleaning", price: 180 },
    { name: "Carpet Cleaning", price: 150 },
  ]},
  { slug: "lawn-care", name: "Lawn Care & Landscaping", group: "Field Service", defaultServices: [
    { name: "Lawn Mowing", price: 50 },
    { name: "Edge & Trim", price: 30 },
    { name: "Hedge Trimming", price: 80 },
    { name: "Leaf Removal", price: 100 },
    { name: "Mulch Install", price: 200 },
  ]},
  { slug: "hvac", name: "HVAC", group: "Field Service", defaultServices: [
    { name: "Diagnostic Visit", price: 89 },
    { name: "AC Tune-Up", price: 129 },
    { name: "Filter Replacement", price: 40 },
    { name: "Refrigerant Recharge", price: 250 },
    { name: "Furnace Inspection", price: 99 },
  ]},
  { slug: "plumbing", name: "Plumbing", group: "Field Service", defaultServices: [
    { name: "Service Call", price: 95 },
    { name: "Drain Cleaning", price: 175 },
    { name: "Faucet Install", price: 150 },
    { name: "Water Heater Install", price: 1200 },
    { name: "Leak Repair", price: 200 },
  ]},
  { slug: "electrical", name: "Electrical", group: "Field Service", defaultServices: [
    { name: "Service Call", price: 110 },
    { name: "Outlet Install", price: 150 },
    { name: "Panel Inspection", price: 200 },
    { name: "Light Fixture Install", price: 175 },
    { name: "Ceiling Fan Install", price: 250 },
  ]},
  { slug: "pest-control", name: "Pest Control", group: "Field Service", defaultServices: [
    { name: "Initial Treatment", price: 175 },
    { name: "Monthly Service", price: 65 },
    { name: "Termite Inspection", price: 125 },
    { name: "Rodent Removal", price: 250 },
  ]},
  { slug: "pool-service", name: "Pool Service", group: "Field Service", defaultServices: [
    { name: "Weekly Maintenance", price: 120 },
    { name: "Pool Opening", price: 350 },
    { name: "Pool Closing", price: 300 },
    { name: "Equipment Repair", price: 200 },
  ]},
  { slug: "auto-detailing", name: "Auto Detailing", group: "Field Service", defaultServices: [
    { name: "Basic Wash", price: 35 },
    { name: "Full Detail", price: 175 },
    { name: "Interior Detail", price: 100 },
    { name: "Ceramic Coating", price: 600 },
    { name: "Headlight Restoration", price: 75 },
  ]},
  { slug: "window-washing", name: "Window Washing", group: "Field Service", defaultServices: [
    { name: "Residential (per window)", price: 8 },
    { name: "Commercial Storefront", price: 150 },
    { name: "Screen Cleaning", price: 5 },
    { name: "Track & Sill Detail", price: 4 },
  ]},
  { slug: "painting", name: "Painting", group: "Field Service", defaultServices: [
    { name: "Interior Room", price: 450 },
    { name: "Exterior House", price: 3500 },
    { name: "Cabinet Painting", price: 1500 },
    { name: "Trim Only", price: 250 },
  ]},
  { slug: "roofing", name: "Roofing", group: "Field Service", defaultServices: [
    { name: "Inspection", price: 175 },
    { name: "Repair", price: 500 },
    { name: "Full Replacement", price: 8500 },
    { name: "Gutter Cleaning", price: 150 },
  ]},
  { slug: "handyman", name: "Handyman", group: "Field Service", defaultServices: [
    { name: "Hourly Labor", price: 75, unit: "hr" },
    { name: "TV Mounting", price: 125 },
    { name: "Furniture Assembly", price: 100 },
    { name: "Drywall Repair", price: 175 },
  ]},
  { slug: "junk-removal", name: "Junk Removal", group: "Field Service", defaultServices: [
    { name: "Quarter Truck Load", price: 175 },
    { name: "Half Truck Load", price: 300 },
    { name: "Full Truck Load", price: 500 },
    { name: "Single Item Pickup", price: 75 },
  ]},
  { slug: "snow-removal", name: "Snow Removal", group: "Field Service", defaultServices: [
    { name: "Driveway Plow", price: 60 },
    { name: "Walkway Shovel", price: 35 },
    { name: "Seasonal Contract", price: 600 },
    { name: "Salting", price: 30 },
  ]},
  { slug: "moving", name: "Moving Services", group: "Field Service", defaultServices: [
    { name: "Local Move (hourly)", price: 150, unit: "hr" },
    { name: "Loading Only", price: 350 },
    { name: "Packing Service", price: 250 },
    { name: "Long Distance Move", price: 2500 },
  ]},
  { slug: "photography", name: "Photography", group: "Creative", defaultServices: [
    { name: "Headshot Session", price: 250 },
    { name: "Family Session", price: 450 },
    { name: "Wedding Coverage", price: 2500 },
    { name: "Real Estate Shoot", price: 200 },
  ]},
  { slug: "videography", name: "Videography", group: "Creative", defaultServices: [
    { name: "Event Coverage", price: 1200 },
    { name: "Promo Video", price: 1500 },
    { name: "Wedding Film", price: 3000 },
    { name: "Drone Footage", price: 400 },
  ]},
  { slug: "catering", name: "Catering", group: "Hospitality", defaultServices: [
    { name: "Per-Person Plated", price: 35 },
    { name: "Buffet Setup", price: 28 },
    { name: "Appetizer Tray", price: 75 },
    { name: "Bar Service (per hr)", price: 200, unit: "hr" },
  ]},
  { slug: "restaurant", name: "Restaurant", group: "Hospitality", defaultServices: [
    { name: "Private Event Booking", price: 500 },
    { name: "Group Reservation", price: 0 },
    { name: "Catering Order", price: 250 },
  ]},
  { slug: "cafe-bakery", name: "Cafe & Bakery", group: "Hospitality", defaultServices: [
    { name: "Custom Cake", price: 75 },
    { name: "Catering Tray", price: 45 },
    { name: "Wedding Cake", price: 350 },
  ]},
  { slug: "salon", name: "Hair & Nail Salon", group: "Personal Care", defaultServices: [
    { name: "Haircut", price: 45 },
    { name: "Color & Highlights", price: 150 },
    { name: "Manicure", price: 35 },
    { name: "Pedicure", price: 50 },
    { name: "Blowout", price: 55 },
  ]},
  { slug: "spa", name: "Spa & Massage", group: "Personal Care", defaultServices: [
    { name: "60-Min Massage", price: 110 },
    { name: "90-Min Massage", price: 160 },
    { name: "Facial", price: 95 },
    { name: "Body Scrub", price: 130 },
  ]},
  { slug: "barbershop", name: "Barbershop", group: "Personal Care", defaultServices: [
    { name: "Men's Cut", price: 30 },
    { name: "Beard Trim", price: 20 },
    { name: "Cut + Beard", price: 45 },
    { name: "Hot Towel Shave", price: 40 },
  ]},
  { slug: "personal-trainer", name: "Personal Training", group: "Fitness", defaultServices: [
    { name: "1-on-1 Session", price: 80 },
    { name: "10-Session Pack", price: 700 },
    { name: "Group Training", price: 35 },
    { name: "Online Coaching (mo)", price: 200 },
  ]},
  { slug: "gym", name: "Gym & Fitness Studio", group: "Fitness", defaultServices: [
    { name: "Monthly Membership", price: 49 },
    { name: "Annual Membership", price: 499 },
    { name: "Drop-In Class", price: 25 },
    { name: "Personal Training Add-On", price: 150 },
  ]},
  { slug: "yoga-pilates", name: "Yoga & Pilates Studio", group: "Fitness", defaultServices: [
    { name: "Drop-In Class", price: 22 },
    { name: "10-Class Pack", price: 180 },
    { name: "Monthly Unlimited", price: 149 },
    { name: "Private Session", price: 95 },
  ]},
  { slug: "tutoring", name: "Tutoring & Education", group: "Professional", defaultServices: [
    { name: "1-on-1 Hour", price: 60, unit: "hr" },
    { name: "Test Prep Package", price: 800 },
    { name: "Group Session", price: 30 },
  ]},
  { slug: "consulting", name: "Consulting", group: "Professional", defaultServices: [
    { name: "Discovery Call", price: 0 },
    { name: "Hourly Consulting", price: 250, unit: "hr" },
    { name: "Strategy Workshop", price: 2500 },
    { name: "Monthly Retainer", price: 5000 },
  ]},
  { slug: "marketing-agency", name: "Marketing Agency", group: "Professional", defaultServices: [
    { name: "SEO Audit", price: 1500 },
    { name: "Monthly SEO", price: 2500 },
    { name: "Ad Management", price: 1500 },
    { name: "Social Media Mgmt", price: 1200 },
  ]},
  { slug: "web-design", name: "Web Design & Development", group: "Professional", defaultServices: [
    { name: "Landing Page", price: 1500 },
    { name: "5-Page Website", price: 3500 },
    { name: "E-commerce Site", price: 8500 },
    { name: "Monthly Maintenance", price: 200 },
  ]},
  { slug: "accounting-firm", name: "Accounting & Bookkeeping", group: "Professional", defaultServices: [
    { name: "Monthly Bookkeeping", price: 350 },
    { name: "Tax Return - Personal", price: 350 },
    { name: "Tax Return - Business", price: 850 },
    { name: "Payroll Service (mo)", price: 150 },
  ]},
  { slug: "legal-services", name: "Legal Services", group: "Professional", defaultServices: [
    { name: "Consultation", price: 250 },
    { name: "Contract Review", price: 500 },
    { name: "Hourly Rate", price: 350, unit: "hr" },
    { name: "Will & Trust Package", price: 1200 },
  ]},
  { slug: "real-estate", name: "Real Estate", group: "Professional", defaultServices: [
    { name: "Listing Commission", price: 0 },
    { name: "Buyer Representation", price: 0 },
    { name: "Property Valuation", price: 350 },
  ]},
  { slug: "property-management", name: "Property Management", group: "Professional", defaultServices: [
    { name: "Monthly Mgmt Fee (%)", price: 8 },
    { name: "Tenant Placement", price: 750 },
    { name: "Lease Renewal", price: 250 },
  ]},
  { slug: "insurance", name: "Insurance Agency", group: "Professional", defaultServices: [
    { name: "Policy Quote", price: 0 },
    { name: "Annual Review", price: 0 },
  ]},
  { slug: "financial-planning", name: "Financial Planning", group: "Professional", defaultServices: [
    { name: "Initial Consultation", price: 0 },
    { name: "Financial Plan", price: 2500 },
    { name: "Hourly Advice", price: 300, unit: "hr" },
  ]},
  { slug: "event-planning", name: "Event Planning", group: "Hospitality", defaultServices: [
    { name: "Day-of Coordination", price: 1500 },
    { name: "Full Event Planning", price: 5000 },
    { name: "Corporate Event", price: 3500 },
  ]},
  { slug: "dj-entertainment", name: "DJ & Entertainment", group: "Hospitality", defaultServices: [
    { name: "Wedding DJ Package", price: 1800 },
    { name: "Corporate Event", price: 1200 },
    { name: "Birthday Party (4 hrs)", price: 800 },
  ]},
  { slug: "florist", name: "Florist", group: "Retail", defaultServices: [
    { name: "Bridal Bouquet", price: 175 },
    { name: "Centerpiece", price: 85 },
    { name: "Funeral Arrangement", price: 150 },
    { name: "Delivery", price: 25 },
  ]},
  { slug: "boutique-retail", name: "Boutique & Retail", group: "Retail", defaultServices: [
    { name: "Personal Styling", price: 100 },
    { name: "Alterations", price: 35 },
  ]},
  { slug: "jewelry", name: "Jewelry", group: "Retail", defaultServices: [
    { name: "Ring Resizing", price: 65 },
    { name: "Custom Design", price: 500 },
    { name: "Repair", price: 85 },
    { name: "Appraisal", price: 75 },
  ]},
  { slug: "automotive-repair", name: "Auto Repair", group: "Automotive", defaultServices: [
    { name: "Diagnostic", price: 110 },
    { name: "Oil Change", price: 65 },
    { name: "Brake Service", price: 350 },
    { name: "Tire Rotation", price: 40 },
  ]},
  { slug: "dental", name: "Dental Practice", group: "Healthcare", defaultServices: [
    { name: "Cleaning & Exam", price: 175 },
    { name: "Filling", price: 250 },
    { name: "Crown", price: 1200 },
    { name: "X-Rays", price: 95 },
  ]},
  { slug: "chiropractor", name: "Chiropractor", group: "Healthcare", defaultServices: [
    { name: "Initial Consultation", price: 125 },
    { name: "Adjustment", price: 75 },
    { name: "Massage Therapy", price: 95 },
  ]},
  { slug: "therapy-counseling", name: "Therapy & Counseling", group: "Healthcare", defaultServices: [
    { name: "Individual Session", price: 150 },
    { name: "Couples Session", price: 200 },
    { name: "Group Therapy", price: 60 },
  ]},
  { slug: "veterinary", name: "Veterinary", group: "Healthcare", defaultServices: [
    { name: "Wellness Exam", price: 75 },
    { name: "Vaccination", price: 35 },
    { name: "Dental Cleaning", price: 350 },
    { name: "Surgery Consultation", price: 125 },
  ]},
  { slug: "pet-grooming", name: "Pet Grooming & Boarding", group: "Personal Care", defaultServices: [
    { name: "Full Groom (Small)", price: 55 },
    { name: "Full Groom (Large)", price: 95 },
    { name: "Bath & Brush", price: 40 },
    { name: "Boarding (per night)", price: 45 },
  ]},
  { slug: "construction", name: "General Construction", group: "Construction", defaultServices: [
    { name: "Site Estimate", price: 0 },
    { name: "Hourly Labor", price: 85, unit: "hr" },
    { name: "Demolition", price: 1500 },
    { name: "Permit Pull", price: 350 },
  ]},
  { slug: "flooring", name: "Flooring", group: "Construction", defaultServices: [
    { name: "Hardwood Install (per sq ft)", price: 8, unit: "sq ft" },
    { name: "Tile Install (per sq ft)", price: 10, unit: "sq ft" },
    { name: "Carpet Install (per sq ft)", price: 4, unit: "sq ft" },
    { name: "Floor Refinishing", price: 1200 },
  ]},
  { slug: "other", name: "Other / General Service", group: "Other", defaultServices: [
    { name: "Service Call", price: 100 },
    { name: "Hourly Rate", price: 75, unit: "hr" },
  ]},
];

export type IndustrySlug = typeof INDUSTRIES[number]["slug"];

export const INDUSTRY_GROUPS = Array.from(new Set(INDUSTRIES.map((i) => i.group)));

export const getIndustryName = (slug?: string | null) =>
  INDUSTRIES.find((i) => i.slug === slug)?.name ?? "Not set";

export const getDefaultServices = (slug?: string | null): DefaultService[] =>
  INDUSTRIES.find((i) => i.slug === slug)?.defaultServices ?? [];
